import type { PhoneNumberMeta } from '@team-milva/utils/phone'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { LabAccount } from '@team-milva/lab-models/labAccount'
import type { IOrderPriorityConfig } from '@team-milva/lab-models/labAccount'
import { LabManagementUser } from '@/globals/typescript/models/LabManagementUser'
import { applyColorTheme } from '@/globals/typescript/utils/themeUtils'
import { i18n } from '@/locales'
import { useInviteStore } from '@/stores/inviteStore'
import { useUserStore } from '@/stores/userStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useTextListStore } from '@/stores/textListStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useOrderStore } from '@/stores/orderStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useAnalysisResultStore } from '@/stores/analysisResultStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useDisplayStore } from '@/stores/displayStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useCustomerPricingFilterStore } from '@/stores/customerPricingFilterStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { useDhlShipmentStore } from '@/stores/dhlShipmentStore'
import { useBcSettingsStore } from '@/stores/bcSettingsStore'
import { useBusinessCentralStore } from '@/stores/businessCentralStore'
import { useInvoiceStore } from '@/stores/invoiceStore'

import {
  LAB_CUSTOMER_LANGUAGES,
  LAB_MANAGEMENT_LANGUAGES,
  type ILabLanguage,
  type TLabCustomerLanguageCode,
  type TLabManagementLanguageCode,
} from '@team-milva/lab-models/language'
import {
  LAB_MANAGEMENT_CURRENCIES,
  type ILabCurrency,
  type TLabManagementCurrencyCode,
} from '@team-milva/lab-models/currency'
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore'
import { sortBy } from 'lodash-es'
import { defineStore } from 'pinia'

export interface IDhlSettingsData {
  isEnabled: boolean
  accountNumber: string
  apiUsername: string
  destinationAddress: {
    addressLine: string
    city: string
    postalCode: string
    countryCode: string
    companyName: string
    contactName: string
    phone: PhoneNumberMeta
    email: string
  }
  options?: { earlyDelivery: boolean }
}

export const useAccountStore = defineStore('account', {
  state: () => ({
    partialLabAccount: null as LabAccount | null,
    labAccount: null as LabAccount | null,
    allLabAccountUsers: [] as LabManagementUser[],
    allLabAccounts: [] as LabAccount[],

    // Credentials never live on `labAccount` (see `ILabAccountDhlIntegration`) — this is fetched
    // on demand via `getDhlSettings` from the backend-only Integrations doc instead.
    dhlSettings: null as IDhlSettingsData | null,
    isDhlSettingsLoaded: false,

    unsubscribeLabAccount: null as (() => void) | null,
    unsubscribeLabAccountUsers: null as (() => void) | null,
    unsubscribeAllLabAccounts: null as (() => void) | null,
  }),

  actions: {
    getPartialLabAccount(labSlug: string): Promise<LabAccount | null> {
      if (
        this.partialLabAccount &&
        this.partialLabAccount.subDomain === labSlug
      ) {
        return Promise.resolve(this.partialLabAccount)
      }

      const q = query(
        collection(db, 'Apps', '-lab-management-', 'LabAccounts'),
        where('subDomain', '==', labSlug)
      )

      return getDocs(q).then((querySnapshot) => {
        if (querySnapshot.empty || !querySnapshot.docs[0]) {
          this.partialLabAccount = null
          return null
        }

        const labAccount = new LabAccount(
          querySnapshot.docs[0].data() as LabAccount
        )
        this.partialLabAccount = labAccount

        applyColorTheme(
        labAccount.colorTheme,
        labAccount.themes?.labManagementColors
      )
        return labAccount
      })
    },

    getLabAccountFromUser(): Promise<LabAccount | null> {
      if (this.labAccountLoaded) {
        return Promise.resolve(this.labAccount)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return Promise.resolve(null)
        }

        let resolved = false
        const unsubscribe = onSnapshot(
          doc(
            db,
            'Apps',
            '-lab-management-',
            'LabAccounts',
            userStore.currentUser.accountID
          ),
          (doc) => {
            const labAccount = new LabAccount(doc.data() as LabAccount)

            if (labAccount) {
              this.labAccount = labAccount
              this.unsubscribeLabAccount = unsubscribe

              applyColorTheme(
        labAccount.colorTheme,
        labAccount.themes?.labManagementColors
      )

              // Only resolve once
              if (!resolved) {
                resolved = true
                resolve(this.labAccount)
              }
            }
          }
        )
      })
    },

    getLabAccountFromSlug(labSlug: string): Promise<LabAccount | null> {
      if (this.labAccountLoaded && this.labAccount?.subDomain === labSlug) {
        return Promise.resolve(this.labAccount)
      }

      // Check to reset all needed stores when switching accounts
      if (this.labAccountLoaded && this.labAccount?.subDomain !== labSlug) {
        const inviteStore = useInviteStore()
        const textListStore = useTextListStore()

        // An impersonated employee belongs to one specific lab — always
        // exit impersonation on lab switch. ChangeAccountModal.vue already
        // does this for its own switch action; this covers any other path
        // that changes the loaded lab (e.g. a direct URL/slug change).
        useUserStore().setImpersonationNone()

        inviteStore.resetStore()
        useAnalysisTestStore().resetStore()
        useAnalysisStore().resetStore()
        useTurnaroundTimeStore().resetStore()
        useClosedDayStore().resetStore()
        textListStore.resetStore()
        usePricingSegmentStore().resetStore()
        useAddonServiceStore().resetStore()
        useCustomerAccountStore().resetStore()
        useInsuranceCustomerAccountStore().resetStore()
        useOrderStore().resetStore()
        useCustomerUserStore().resetStore()
        useAnalysisResultStore().resetStore()
        useReportTemplateStore().resetStore()
        useDisplayStore().resetStore()
        useSampleAreaStore().resetStore()
        useDropPointStore().resetStore()
        useDhlShipmentStore().resetStore()
        useLabEquipmentStore().resetStore()
        useLabLocationStore().resetStore()
        useReportDescriptionStore().resetStore()
        // Holds segment IDs and currency codes, both lab-scoped — carried into
        // another lab they match nothing, and its customer lists come up empty
        // with no clue why until the user finds "Nulstil filtre".
        useCustomerPricingFilterStore().resetStore()
        useReportDescriptionItemStore().resetStore()
        useBilagLibraryItemStore().resetStore()
        useBcSettingsStore().resetStore()
        useBusinessCentralStore().resetStore()
        useInvoiceStore().resetStore()
        this.resetStore()
      }

      const q = query(
        collection(db, 'Apps', '-lab-management-', 'LabAccounts'),
        where('subDomain', '==', labSlug)
      )

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          if (querySnapshot.empty || !querySnapshot.docs[0]) {
            // An empty snapshot straight from local cache does not mean the
            // lab doesn't exist — it means this query hasn't been cached yet.
            // Wait for the server-confirmed snapshot before treating it as
            // "not found", otherwise a fresh app init (e.g. after an HMR
            // full reload) can momentarily resolve null and redirect away.
            if (querySnapshot.metadata.fromCache) {
              return
            }
            this.labAccount = null
            return resolve(null)
          }

          const labAccount = new LabAccount(
            querySnapshot.docs[0].data() as LabAccount
          )
          this.labAccount = labAccount
          this.unsubscribeLabAccount = unsubscribe

          applyColorTheme(
        labAccount.colorTheme,
        labAccount.themes?.labManagementColors
      )

          // Only resolve once
          if (!resolved) {
            resolved = true
            resolve(labAccount)
          }
        })
      })
    },

    getAllLabAccountUsers(): Promise<LabManagementUser[]> {
      if (this.allLabAccountUsersLoaded) {
        return Promise.resolve(this.allLabAccountUsers)
      }

      return new Promise((resolve) => {
        let resolved = false
        if (!this.labAccount) {
          return Promise.resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'Users'),
          where('accountID', '==', this.labAccount.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const users: LabManagementUser[] = []
          querySnapshot.forEach((doc) => {
            users.push(new LabManagementUser(doc.data() as LabManagementUser))
          })
          this.allLabAccountUsers = users

          this.unsubscribeLabAccountUsers = unsubscribe

          // Only resolve once
          if (!resolved) {
            resolved = true
            resolve(users)
          }
        })
      })
    },

    getAllLabAccounts(): Promise<LabAccount[]> {
      if (this.allLabAccountsLoaded) {
        return Promise.resolve(this.allLabAccounts)
      }

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(
          collection(db, 'Apps', '-lab-management-', 'LabAccounts'),
          (querySnapshot) => {
            const labAccounts: LabAccount[] = []
            querySnapshot.forEach((doc) => {
              labAccounts.push(new LabAccount(doc.data() as LabAccount))
            })
            this.allLabAccounts = sortBy(labAccounts, ['name'])
            this.unsubscribeAllLabAccounts = unsubscribe

            // Only resolve once
            if (!resolved) {
              resolved = true
              resolve(labAccounts)
            }
          }
        )
      })
    },

    async updateReportSettings(params: {
      laboratoryManagerUserID: string | null
      phone: PhoneNumberMeta | null
      email: string | null
    }): Promise<void> {
      if (!this.labAccount) return

      const result = await managementApi('patch', '/labs/{labID}', {
        path: { labID: this.labAccount.id },
        body: { reportSettings: params },
      })
      if (!result.success) throw new Error(result.code)
    },

    async updateOrderPriorityConfig(
      config: IOrderPriorityConfig
    ): Promise<void> {
      if (!this.labAccount) return

      const result = await managementApi('patch', '/labs/{labID}', {
        path: { labID: this.labAccount.id },
        body: { orderPriorityConfig: config },
      })
      if (!result.success) throw new Error(result.code)
    },

    // Idempotent — safe to call on every settings-page mount. Goes through managementApi rather
    // than a direct Firestore read (unlike bcSettingsStore.loadSettings) because the underlying
    // Integrations/{id} doc holds encrypted credentials — this endpoint deliberately omits the
    // encrypted password from its response, and the client must never read that doc directly.
    async getDhlSettings(): Promise<void> {
      if (this.isDhlSettingsLoaded || !this.labAccount) return

      const result = await managementApi(
        'get',
        '/lab-accounts/{labAccountID}/integrations/dhl',
        { path: { labAccountID: this.labAccount.id } }
      )
      if (!result.success) throw new Error(result.code)

      this.dhlSettings = result.data.dhlSettings
      this.isDhlSettingsLoaded = true
    },

    async updateDhlSettings(params: {
      accountNumber?: string
      apiUsername?: string
      apiPassword?: string
      destinationAddress?: {
        addressLine: string
        city: string
        postalCode: string
        countryCode: string
        companyName: string
        contactName: string
        phone: PhoneNumberMeta
        email: string
      }
      options?: {
        earlyDelivery: boolean
      }
      isEnabled?: boolean
    }) {
      if (!this.labAccount) {
        return
      }

      const result = await managementApi(
        'patch',
        '/lab-accounts/{labAccountID}/integrations/dhl',
        {
          path: { labAccountID: this.labAccount.id },
          // TODO: remove cast after deploying and re-running openapi:gen — codegen doesn't have
          // isEnabled yet, since dev hasn't been deployed with this change.
          body: params as unknown as {
            accountNumber?: string
            apiUsername?: string
            apiPassword?: string
            destinationAddress?: {
              addressLine: string
              city: string
              postalCode: string
              countryCode: string
              companyName: string
              contactName: string
              phone: PhoneNumberMeta
              email: string
            }
            options?: { earlyDelivery: boolean }
          },
        }
      )
      if (!result.success) throw new Error(result.code)

      // The lightweight labAccount projection updates via its own onSnapshot listener, but the
      // full settings (credentials, address) fetched by getDhlSettings do not — force a refetch.
      this.isDhlSettingsLoaded = false
      await this.getDhlSettings()
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    hydrateFromPublicData(raw: unknown): void {
      const labAccount = new LabAccount(raw as Partial<LabAccount>)
      this.labAccount = labAccount
      applyColorTheme(
        labAccount.colorTheme,
        labAccount.themes?.labManagementColors
      )

      // There is no logged-in user to carry a language preference, and no
      // language selector on a Display (see App.vue) — it always renders in
      // the lab's own configured default language. Never persisted to
      // localStorage: that's a per-user preference mechanism, and this is a
      // shared kiosk browser that must not leak the lab's language into some
      // other session on the same device.
      i18n.global.locale.value =
        LAB_MANAGEMENT_LANGUAGES[labAccount.defaultLanguageCode]?.locale ?? 'en'
    },

    resetStore() {
      this.partialLabAccount = null
      this.labAccount = null
      this.allLabAccountUsers = []
      this.allLabAccounts = []
      this.dhlSettings = null
      this.isDhlSettingsLoaded = false

      if (this.unsubscribeLabAccount) {
        this.unsubscribeLabAccount()
        this.unsubscribeLabAccount = null
      }
      if (this.unsubscribeLabAccountUsers) {
        this.unsubscribeLabAccountUsers()
        this.unsubscribeLabAccountUsers = null
      }
      if (this.unsubscribeAllLabAccounts) {
        this.unsubscribeAllLabAccounts()
        this.unsubscribeAllLabAccounts = null
      }
    },
  },

  getters: {
    // Loaders
    labAccountLoaded: (state) => {
      return !!state.unsubscribeLabAccount
    },
    allLabAccountUsersLoaded: (state) => {
      return !!state.unsubscribeLabAccountUsers
    },
    allLabAccountsLoaded: (state) => {
      return !!state.unsubscribeAllLabAccounts
    },

    // Other
    isCurrentLabAccountDeactivated: (state) => {
      return state.labAccount?.activeState !== 'active'
    },

    availableLanguages: (state): ILabLanguage[] => {
      const account = state.labAccount ?? state.partialLabAccount
      if (!account) {
        return sortBy(Object.values(LAB_MANAGEMENT_LANGUAGES), 'code')
      }
      return sortBy(
        (account as LabAccount).labLanguages
          .map(
            (code) =>
              LAB_MANAGEMENT_LANGUAGES[code as TLabManagementLanguageCode]
          )
          .filter((l) => l !== undefined) as ILabLanguage[],
        'code'
      )
    },

    availableCustomerLanguages: (state): ILabLanguage[] => {
      const account = state.labAccount ?? state.partialLabAccount
      if (!account) {
        return sortBy(Object.values(LAB_CUSTOMER_LANGUAGES), 'code')
      }
      return sortBy(
        (account as LabAccount).customerLanguages
          .map(
            (code) => LAB_CUSTOMER_LANGUAGES[code as TLabCustomerLanguageCode]
          )
          .filter((l) => l !== undefined) as ILabLanguage[],
        'code'
      )
    },

    // The locales user-authored content can be translated into — the union of
    // the lab's own languages and its customer-app languages. Distinct from
    // availableLanguages, which is only the staff UI language set (labLanguages,
    // and therefore never more than DK/EN): report descriptions, report
    // templates and analysis auto-descriptions are rendered into a PDF in the
    // order's reportLocale, which is bounded by customerLanguages. Authoring
    // them against availableLanguages meant a locale the customer actually
    // receives could never be filled in. EN first (it is the getTextObjValue
    // fallback), then alphabetical.
    availableContentLanguages: (state): ILabLanguage[] => {
      const account = state.labAccount ?? state.partialLabAccount
      const codes = account
        ? [
            ...(account as LabAccount).labLanguages,
            ...(account as LabAccount).customerLanguages,
          ]
        : [
            ...Object.keys(LAB_MANAGEMENT_LANGUAGES),
            ...Object.keys(LAB_CUSTOMER_LANGUAGES),
          ]

      const seen = new Set<string>()
      const languages: ILabLanguage[] = []
      for (const code of codes) {
        if (seen.has(code)) continue
        seen.add(code)
        const language =
          LAB_MANAGEMENT_LANGUAGES[code as TLabManagementLanguageCode] ??
          LAB_CUSTOMER_LANGUAGES[code as TLabCustomerLanguageCode]
        if (language) languages.push(language)
      }

      const english = languages.find((x) => x.code === 'EN')
      const rest = sortBy(
        languages.filter((x) => x.code !== 'EN'),
        'name'
      )
      return english ? [english, ...rest] : rest
    },

    availableCurrencies: (state): ILabCurrency[] => {
      const account = state.labAccount ?? state.partialLabAccount
      if (!account) {
        return sortBy(Object.values(LAB_MANAGEMENT_CURRENCIES), 'code')
      }
      return sortBy(
        (account as LabAccount).labCurrencies
          .map(
            (code) =>
              LAB_MANAGEMENT_CURRENCIES[code as TLabManagementCurrencyCode]
          )
          .filter((c) => c !== undefined) as ILabCurrency[],
        'code'
      )
    },

    defaultCurrencyCode: (state): TLabManagementCurrencyCode => {
      return state.labAccount?.defaultCurrencyCode ?? 'DKK'
    },
  },
})
